import type { FileItem } from "../types/file-item.types.ts"
import type { FilesSortOrder } from "../types/files-filters.types.ts"
import type { FolderListItem } from "../types/folder.types.ts"
import { folderItemCount } from "./folder-item-count.ts"

/** Turns whatever the API sent for a date into something comparable. */
export type ParseSortDate = (value: string) => number

interface SortKeys {
  name: string
  created: string
  updated: string
  size: number
}

interface Keyed<T> {
  item: T
  name: string
  created: number
  updated: number
  size: number
}

type Compare = <T>(a: Keyed<T>, b: Keyed<T>) => number

const newestModified: Compare = (a, b) => b.updated - a.updated

const compareBy = (order: FilesSortOrder): Compare => {
  switch (order) {
    case "added_desc":
      return (a, b) => b.created - a.created
    case "name_asc":
      return (a, b) => a.name.localeCompare(b.name)
    case "name_desc":
      return (a, b) => b.name.localeCompare(a.name)
    case "size_desc":
      return (a, b) => b.size - a.size
    case "size_asc":
      return (a, b) => a.size - b.size
    case "modified_desc":
    default:
      return newestModified
  }
}

/** A tie goes to the most recently modified, then keeps the input order: for items
 * rendered to the same minute, that order is the only record of which came last. */
const sortBy = <T>(
  items: T[],
  order: FilesSortOrder,
  parseDate: ParseSortDate,
  keysOf: (item: T) => SortKeys
): T[] => {
  const compare = compareBy(order)
  return items
    .map((item) => {
      const keys = keysOf(item)
      return {
        item,
        name: keys.name,
        created: parseDate(keys.created),
        updated: parseDate(keys.updated),
        size: keys.size
      }
    })
    .sort((a, b) => compare(a, b) || newestModified(a, b))
    .map(({ item }) => item)
}

/** Client-side because album files and documents come from two endpoints. */
export const sortFileItems = (
  items: FileItem[],
  order: FilesSortOrder,
  parseDate: ParseSortDate
): FileItem[] =>
  sortBy(items, order, parseDate, (item) => ({
    name: item.name,
    created: item.created,
    updated: item.updated,
    size: item.fileSize ?? 0
  }))

/** A folder has no byte size, so the size orders read the item count its card shows. */
export const sortFolders = (
  folders: FolderListItem[],
  order: FilesSortOrder,
  parseDate: ParseSortDate
): FolderListItem[] =>
  sortBy(folders, order, parseDate, (folder) => ({
    name: folder.name,
    created: folder.created,
    updated: folder.updated,
    size: folderItemCount(folder)
  }))
